import type { AppError, ValidationError } from '@features/auth/types';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function isFetchBaseQueryError(err: unknown): err is FetchBaseQueryError {
  return typeof err === 'object' && err !== null && 'status' in err;
}

function isSerializedError(err: unknown): err is SerializedError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof (err as { message?: unknown }).message === 'string'
  );
}

/**
 * Normalize any thrown value (from `unwrap()`, `fetch`, etc.) into our `AppError`.
 * Safe to call with unknown shapes — never throws.
 */
export function toAppError(err: unknown): AppError {
  if (isFetchBaseQueryError(err)) {
    return transformError(err);
  }
  if (err instanceof Error) {
    return { status: 'CUSTOM_ERROR', code: 'UNKNOWN', message: err.message, original: err };
  }
  if (isSerializedError(err)) {
    return {
      status: 'CUSTOM_ERROR',
      code: err.code ?? 'UNKNOWN',
      message: err.message ?? 'Something went wrong.',
      original: err,
    };
  }
  return { status: 'CUSTOM_ERROR', code: 'UNKNOWN', message: 'Something went wrong.' };
}

export function transformError(error: FetchBaseQueryError): AppError {
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') {
    return {
      status: error.status,
      code: 'NETWORK_ERROR',
      message: 'Unable to reach the server. Please check your connection.',
      original: error.error,
    };
  }

  if (error.status === 'PARSING_ERROR') {
    return {
      status: 'PARSING_ERROR',
      code: 'PARSING_ERROR',
      message: 'Unexpected response from server.',
      original: error.data,
    };
  }

  if (error.status === 'CUSTOM_ERROR') {
    return {
      status: 'CUSTOM_ERROR',
      code: 'CUSTOM_ERROR',
      message: error.error,
    };
  }

  // Backend contract: `{ error: { code, message, validations } }` (core-service ErrorResponse).
  // Bodies without that envelope (empty 401s from a proxy, HTML error pages) must still map
  // to an AppError rather than throw — callers render `message` straight into the UI.
  const body = (typeof error.data === 'object' && error.data !== null ? error.data : {}) as {
    error?: {
      code?: string | number;
      message?: string;
      validations?: ValidationError[];
    } | null;
  };
  const envelope = body.error ?? {};

  return {
    status: error.status,
    code: envelope.code != null ? String(envelope.code) : `HTTP_${String(error.status)}`,
    message: envelope.message ?? 'Something went wrong.',
    validations: envelope.validations ?? [],
    original: error.data,
  };
}
